// banking/src/pages/onboarding/corporate/steps/Step6_RepresentativeDocuments.tsx

import React from 'react';
import { getTranslation, type Language } from '../corporate-translations';
import type { CorporateFormData } from '../CorporateOnboarding';
import FileUploader from '../components/FileUploader';

interface Step6Props {
  formData: CorporateFormData;
  updateFormData: (data: Partial<CorporateFormData>) => void;
  language: Language;
  errors: Record<string, string>;
  onBackToList: () => void; // New: go back to Step 4 (representatives list)
  uploadRepresentativeDocument: (file: File, documentType: string, repIndex: number) => Promise<void>;
}

const Step6_RepresentativeDocuments: React.FC<Step6Props> = ({
  formData,
  updateFormData,
  language,
  errors,
  onBackToList,
  uploadRepresentativeDocument,
}) => {
  // Get current representative
  const currentRepIndex = formData.currentRepresentativeIndex;
  const currentRep = formData.representatives[currentRepIndex];

  if (!currentRep) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">
          {language === 'es'
            ? 'Error: No se encontró el representante actual'
            : language === 'fr'
            ? 'Erreur : Représentant actuel introuvable'
            : language === 'de'
            ? 'Fehler: Aktueller Vertreter nicht gefunden'
            : language === 'it'
            ? 'Errore: Rappresentante attuale non trovato'
            : language === 'pt'
            ? 'Erro: Representante atual não encontrado'
            : language === 'nl'
            ? 'Fout: Huidige vertegenwoordiger niet gevonden'
            : language === 'ru'
            ? 'Ошибка: Текущий представитель не найден'
            : language === 'ar'
            ? 'خطأ: لم يتم العثور على الممثل الحالي'
            : 'Error: Current representative not found'
          }
        </p>
      </div>
    );
  }

  const updateRepDocument = (docType: string, file: File | undefined) => {
    const updatedReps = [...formData.representatives];
    updatedReps[currentRepIndex] = {
      ...currentRep,
      documents: {
        ...currentRep.documents,
        [docType]: file,
      },
    };
    updateFormData({ representatives: updatedReps });
  };

  // Determine which documents are needed based on document type
  const needsIdCardDocs = currentRep.documentType === 'ID_CARD';
  const needsDrivingLicenseDocs = currentRep.documentType === 'DRIVING_LICENSE';
  const needsPassportDocs = currentRep.documentType === 'PASSPORT';

  // Check if ALL documents are uploaded for this representative
  const isRepresentativeComplete = (): boolean => {
    if (!currentRep.documents) return false;

    const hasCommonDocs =
      !!currentRep.documents.selfieWithId &&
      !!currentRep.documents.proofOfAddress &&
      !!currentRep.documents.bankStatement;

    if (needsIdCardDocs) {
      return hasCommonDocs &&
        !!currentRep.documents.idCardFront &&
        !!currentRep.documents.idCardBack;
    } else if (needsDrivingLicenseDocs) {
      return hasCommonDocs &&
        !!currentRep.documents.drivingLicenseFront &&
        !!currentRep.documents.drivingLicenseBack;
    } else if (needsPassportDocs) {
      return hasCommonDocs && !!currentRep.documents.passport;
    }
    return false;
  };

  // Count uploaded documents
  const uploadedDocsCount = Object.values(currentRep.documents || {}).filter(Boolean).length;
  const totalDocsNeeded = (needsIdCardDocs || needsDrivingLicenseDocs) ? 5 : 4;

  return (
    <div className="space-y-6">
      {/* Title */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">
          {getTranslation('step6_title', language)}
        </h2>
        <p className="mt-1 text-sm text-gray-600">
          {getTranslation('step6_subtitle', language)}
        </p>
        <p className="mt-2 text-sm font-medium text-[#B3A369]">
          {language === 'es'
            ? `Documentos para: ${currentRep.firstName} ${currentRep.lastName}`
            : language === 'fr'
            ? `Documents pour : ${currentRep.firstName} ${currentRep.lastName}`
            : language === 'de'
            ? `Dokumente für: ${currentRep.firstName} ${currentRep.lastName}`
            : language === 'it'
            ? `Documenti per: ${currentRep.firstName} ${currentRep.lastName}`
            : language === 'pt'
            ? `Documentos para: ${currentRep.firstName} ${currentRep.lastName}`
            : language === 'nl'
            ? `Documenten voor: ${currentRep.firstName} ${currentRep.lastName}`
            : language === 'ru'
            ? `Документы для: ${currentRep.firstName} ${currentRep.lastName}`
            : language === 'ar'
            ? `المستندات لـ: ${currentRep.firstName} ${currentRep.lastName}`
            : `Documents for: ${currentRep.firstName} ${currentRep.lastName}`
          }
        </p>
      </div>

      {/* Info box */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-yellow-400"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">
              {language === 'es'
                ? 'Documentos requeridos'
                : language === 'fr'
                ? 'Documents requis'
                : language === 'de'
                ? 'Erforderliche Dokumente'
                : language === 'it'
                ? 'Documenti richiesti'
                : language === 'pt'
                ? 'Documentos necessários'
                : language === 'nl'
                ? 'Vereiste documenten'
                : language === 'ru'
                ? 'Требуемые документы'
                : language === 'ar'
                ? 'الوثائق المطلوبة'
                : 'Required documents'
              }
            </h3>
            <div className="mt-2 text-sm text-yellow-700">
              <ul className="list-disc list-inside space-y-1">
                <li>
                  {language === 'es'
                    ? `Todos los ${totalDocsNeeded} documentos son obligatorios`
                    : language === 'fr'
                    ? `Les ${totalDocsNeeded} documents sont obligatoires`
                    : language === 'de'
                    ? `Alle ${totalDocsNeeded} Dokumente sind erforderlich`
                    : language === 'it'
                    ? `Tutti i ${totalDocsNeeded} documenti sono obbligatori`
                    : language === 'pt'
                    ? `Todos os ${totalDocsNeeded} documentos são obrigatórios`
                    : language === 'nl'
                    ? `Alle ${totalDocsNeeded} documenten zijn verplicht`
                    : language === 'ru'
                    ? `Все ${totalDocsNeeded} документа обязательны`
                    : language === 'ar'
                    ? `جميع المستندات ${totalDocsNeeded} مطلوبة`
                    : `All ${totalDocsNeeded} documents are required`
                  }
                </li>
                <li>
                  {language === 'es'
                    ? 'Formatos aceptados: PDF, JPG, JPEG, PNG'
                    : language === 'fr'
                    ? 'Formats acceptés : PDF, JPG, JPEG, PNG'
                    : language === 'de'
                    ? 'Akzeptierte Formate: PDF, JPG, JPEG, PNG'
                    : language === 'it'
                    ? 'Formati accettati: PDF, JPG, JPEG, PNG'
                    : language === 'pt'
                    ? 'Formatos aceitos: PDF, JPG, JPEG, PNG'
                    : language === 'nl'
                    ? 'Geaccepteerde formaten: PDF, JPG, JPEG, PNG'
                    : language === 'ru'
                    ? 'Допустимые форматы: PDF, JPG, JPEG, PNG'
                    : language === 'ar'
                    ? 'التنسيقات المقبولة: PDF، JPG، JPEG، PNG'
                    : 'Accepted formats: PDF, JPG, JPEG, PNG'
                  }
                </li>
                <li>
                  {language === 'es'
                    ? 'Tamaño máximo: 10MB por archivo'
                    : language === 'fr'
                    ? 'Taille maximale : 10 Mo par fichier'
                    : language === 'de'
                    ? 'Maximale Größe: 10 MB pro Datei'
                    : language === 'it'
                    ? 'Dimensione massima: 10 MB per file'
                    : language === 'pt'
                    ? 'Tamanho máximo: 10 MB por arquivo'
                    : language === 'nl'
                    ? 'Maximale grootte: 10 MB per bestand'
                    : language === 'ru'
                    ? 'Максимальный размер: 10 МБ на файл'
                    : language === 'ar'
                    ? 'الحجم الأقصى: 10 ميجابايت لكل ملف'
                    : 'Maximum size: 10MB per file'
                  }
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Document uploads based on document type */}

      {/* ID Card Documents (5 total) */}
      {needsIdCardDocs && (
        <>
          {/* Document 1: ID Card Front */}
          <div>
            <FileUploader
              value={currentRep.documents?.idCardFront}
              onChange={(file) => updateRepDocument('idCardFront', file)}
              onFileSelected={async (file) => await uploadRepresentativeDocument(file, 'idCardFront', currentRepIndex)}
              language={language}
              error={errors[`rep${currentRepIndex}_idCardFront`]}
              label={getTranslation('id_card_front_label', language)}
              required={true}
              accept=".pdf,.jpg,.jpeg,.png"
              maxSizeMB={10}
            />
          </div>

          {/* Document 2: ID Card Back */}
          <div>
            <FileUploader
              value={currentRep.documents?.idCardBack}
              onChange={(file) => updateRepDocument('idCardBack', file)}
              onFileSelected={async (file) => await uploadRepresentativeDocument(file, 'idCardBack', currentRepIndex)}
              language={language}
              error={errors[`rep${currentRepIndex}_idCardBack`]}
              label={getTranslation('id_card_back_label', language)}
              required={true}
              accept=".pdf,.jpg,.jpeg,.png"
              maxSizeMB={10}
            />
          </div>
        </>
      )}

      {/* Driving License Documents (5 total) */}
      {needsDrivingLicenseDocs && (
        <>
          {/* Document 1: Driving License Front */}
          <div>
            <FileUploader
              value={currentRep.documents?.drivingLicenseFront}
              onChange={(file) => updateRepDocument('drivingLicenseFront', file)}
              onFileSelected={async (file) => await uploadRepresentativeDocument(file, 'drivingLicenseFront', currentRepIndex)}
              language={language}
              error={errors[`rep${currentRepIndex}_drivingLicenseFront`]}
              label={getTranslation('driving_license_front_label', language)}
              required={true}
              accept=".pdf,.jpg,.jpeg,.png"
              maxSizeMB={10}
            />
          </div>

          {/* Document 2: Driving License Back */}
          <div>
            <FileUploader
              value={currentRep.documents?.drivingLicenseBack}
              onChange={(file) => updateRepDocument('drivingLicenseBack', file)}
              onFileSelected={async (file) => await uploadRepresentativeDocument(file, 'drivingLicenseBack', currentRepIndex)}
              language={language}
              error={errors[`rep${currentRepIndex}_drivingLicenseBack`]}
              label={getTranslation('driving_license_back_label', language)}
              required={true}
              accept=".pdf,.jpg,.jpeg,.png"
              maxSizeMB={10}
            />
          </div>
        </>
      )}

      {/* Passport Documents (if passport selected) */}
      {needsPassportDocs && (
        <div>
          <FileUploader
            value={currentRep.documents?.passport}
            onChange={(file) => updateRepDocument('passport', file)}
            onFileSelected={async (file) => await uploadRepresentativeDocument(file, 'passport', currentRepIndex)}
            language={language}
            error={errors[`rep${currentRepIndex}_passport`]}
            label={getTranslation('passport_label', language)}
            required={true}
            accept=".pdf,.jpg,.jpeg,.png"
            maxSizeMB={10}
          />
        </div>
      )}

      {/* Common Documents (for both ID types) */}

      {/* Document: Selfie with ID */}
      <div>
        <FileUploader
          value={currentRep.documents?.selfieWithId}
          onChange={(file) => updateRepDocument('selfieWithId', file)}
          onFileSelected={async (file) => await uploadRepresentativeDocument(file, 'selfieWithId', currentRepIndex)}
          language={language}
          error={errors[`rep${currentRepIndex}_selfieWithId`]}
          label={getTranslation('selfie_with_id_label', language)}
          required={true}
          accept=".jpg,.jpeg,.png"
          maxSizeMB={10}
        />
      </div>

      {/* Document: Proof of Address */}
      <div>
        <FileUploader
          value={currentRep.documents?.proofOfAddress}
          onChange={(file) => updateRepDocument('proofOfAddress', file)}
          onFileSelected={async (file) => await uploadRepresentativeDocument(file, 'proofOfAddress', currentRepIndex)}
          language={language}
          error={errors[`rep${currentRepIndex}_proofOfAddress`]}
          label={getTranslation('proof_of_address_label', language)}
          required={true}
          accept=".pdf,.jpg,.jpeg,.png"
          maxSizeMB={10}
        />
        <p className="mt-1 text-xs text-gray-500">
          {language === 'es'
            ? 'Factura de servicios (luz, agua, gas, teléfono) de los últimos 3 meses'
            : language === 'fr'
            ? 'Facture de services publics (électricité, eau, gaz, téléphone) des 3 derniers mois'
            : language === 'de'
            ? 'Versorgungsrechnung (Strom, Wasser, Gas, Telefon) der letzten 3 Monate'
            : language === 'it'
            ? 'Bolletta (luce, acqua, gas, telefono) degli ultimi 3 mesi'
            : language === 'pt'
            ? 'Conta de serviços (luz, água, gás, telefone) dos últimos 3 meses'
            : language === 'nl'
            ? 'Nutsrekening (elektriciteit, water, gas, telefoon) van de afgelopen 3 maanden'
            : language === 'ru'
            ? 'Счет за коммунальные услуги (свет, вода, газ, телефон) за последние 3 месяца'
            : language === 'ar'
            ? 'فاتورة خدمات (كهرباء، ماء، غاز، هاتف) من آخر 3 أشهر'
            : 'Utility bill (electricity, water, gas, phone) from the last 3 months'
          }
        </p>
      </div>

      {/* Document: Bank Statement */}
      <div>
        <FileUploader
          value={currentRep.documents?.bankStatement}
          onChange={(file) => updateRepDocument('bankStatement', file)}
          onFileSelected={async (file) => await uploadRepresentativeDocument(file, 'bankStatement', currentRepIndex)}
          language={language}
          error={errors[`rep${currentRepIndex}_bankStatement`]}
          label={getTranslation('bank_statement_label', language)}
          required={true}
          accept=".pdf,.jpg,.jpeg,.png"
          maxSizeMB={10}
        />
        <p className="mt-1 text-xs text-gray-500">
          {language === 'es'
            ? 'Estado de cuenta bancario de los últimos 3 meses'
            : language === 'fr'
            ? 'Relevé bancaire des 3 derniers mois'
            : language === 'de'
            ? 'Kontoauszug der letzten 3 Monate'
            : language === 'it'
            ? 'Estratto conto bancario degli ultimi 3 mesi'
            : language === 'pt'
            ? 'Extrato bancário dos últimos 3 meses'
            : language === 'nl'
            ? 'Bankafschrift van de afgelopen 3 maanden'
            : language === 'ru'
            ? 'Банковская выписка за последние 3 месяца'
            : language === 'ar'
            ? 'كشف حساب بنكي من آخر 3 أشهر'
            : 'Bank statement from the last 3 months'
          }
        </p>
      </div>

      {/* Progress indicator */}
      {uploadedDocsCount > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-green-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-green-700">
                {language === 'es'
                  ? `${uploadedDocsCount} de ${totalDocsNeeded} documentos subidos`
                  : language === 'fr'
                  ? `${uploadedDocsCount} sur ${totalDocsNeeded} documents téléchargés`
                  : language === 'de'
                  ? `${uploadedDocsCount} von ${totalDocsNeeded} Dokumenten hochgeladen`
                  : language === 'it'
                  ? `${uploadedDocsCount} di ${totalDocsNeeded} documenti caricati`
                  : language === 'pt'
                  ? `${uploadedDocsCount} de ${totalDocsNeeded} documentos carregados`
                  : language === 'nl'
                  ? `${uploadedDocsCount} van ${totalDocsNeeded} documenten geüpload`
                  : language === 'ru'
                  ? `${uploadedDocsCount} из ${totalDocsNeeded} документов загружено`
                  : language === 'ar'
                  ? `${uploadedDocsCount} من ${totalDocsNeeded} وثائق تم تحميلها`
                  : `${uploadedDocsCount} of ${totalDocsNeeded} documents uploaded`
                }
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Note about document requirements */}
      <div className="bg-[#F5F3E8] border border-[#E5DDB8] rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-[#B3A369]"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-[#9A8B57]">
              {language === 'es'
                ? 'Asegúrese de que todos los documentos sean legibles y estén vigentes. Los documentos borrosos o vencidos serán rechazados.'
                : language === 'fr'
                ? 'Assurez-vous que tous les documents sont lisibles et valides. Les documents flous ou expirés seront rejetés.'
                : language === 'de'
                ? 'Stellen Sie sicher, dass alle Dokumente lesbar und gültig sind. Unscharfe oder abgelaufene Dokumente werden abgelehnt.'
                : language === 'it'
                ? 'Assicurati che tutti i documenti siano leggibili e validi. I documenti sfocati o scaduti verranno rifiutati.'
                : language === 'pt'
                ? 'Certifique-se de que todos os documentos sejam legíveis e válidos. Documentos borrados ou vencidos serão rejeitados.'
                : language === 'nl'
                ? 'Zorg ervoor dat alle documenten leesbaar en geldig zijn. Vage of verlopen documenten worden afgewezen.'
                : language === 'ru'
                ? 'Убедитесь, что все документы читаемы и действительны. Размытые или просроченные документы будут отклонены.'
                : language === 'ar'
                ? 'تأكد من أن جميع المستندات واضحة وصالحة. سيتم رفض المستندات غير الواضحة أو المنتهية الصلاحية.'
                : 'Make sure all documents are readable and valid. Blurry or expired documents will be rejected.'
              }
            </p>
          </div>
        </div>
      </div>

      {/* Single button: Complete Representative and Go to List (only active when ALL docs uploaded) */}
      <div className="flex justify-center pt-4">
        <button
          type="button"
          onClick={onBackToList}
          disabled={!isRepresentativeComplete()}
          className={`inline-flex items-center px-6 py-3 border-2 text-base font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#B3A369] ${
            isRepresentativeComplete()
              ? 'border-[#B3A369] text-white bg-[#B3A369] hover:bg-[#9A8B57]'
              : 'border-gray-300 text-gray-400 bg-gray-100 cursor-not-allowed'
          }`}
        >
          <svg
            className="-ml-1 mr-2 h-5 w-5"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
          {getTranslation('complete_representative_button', language)}
        </button>
      </div>

      {/* Warning if documents incomplete */}
      {!isRepresentativeComplete() && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-yellow-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                {language === 'es'
                  ? `Por favor, sube todos los ${totalDocsNeeded} documentos requeridos antes de continuar.`
                  : language === 'fr'
                  ? `Veuillez télécharger les ${totalDocsNeeded} documents requis avant de continuer.`
                  : language === 'de'
                  ? `Bitte laden Sie alle ${totalDocsNeeded} erforderlichen Dokumente hoch, bevor Sie fortfahren.`
                  : language === 'it'
                  ? `Si prega di caricare tutti i ${totalDocsNeeded} documenti richiesti prima di continuare.`
                  : language === 'pt'
                  ? `Por favor, carregue todos os ${totalDocsNeeded} documentos necessários antes de continuar.`
                  : language === 'nl'
                  ? `Upload alle ${totalDocsNeeded} vereiste documenten voordat u doorgaat.`
                  : language === 'ru'
                  ? `Пожалуйста, загрузите все ${totalDocsNeeded} требуемых документа перед продолжением.`
                  : language === 'ar'
                  ? `يرجى تحميل جميع المستندات ${totalDocsNeeded} المطلوبة قبل المتابعة.`
                  : `Please upload all ${totalDocsNeeded} required documents before continuing.`
                }
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Step6_RepresentativeDocuments;
